import React, {
  useRef, useEffect, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import { FieldUpdateHandlerParam } from 'shared/form';
import * as xtpActions from 'data/xtp/xtp.actions';
import * as xtpSelectors from 'data/xtp/xtp.selectors';
import * as xtpTypes from 'data/xtp/xtp.types';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import { Ref } from '../../components/XtpForm/XtpForm';
import {
  adaptTemplateVisibilityMode,
} from '../../utils/xtpTemplate.adapter';
import {
  getXtpDataToSubmit,
  getXtpAuditDetailsToSubmit,
  getXtpOptionsAuditDetailsToSubmit,
} from '../../utils/xtpDataToSubmit';
import { xtpTemplate } from '../../templates';
import XtpForm from '../../components/XtpForm';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  deliveryFunctionId: state.global.selectedDF,
  xtps: xtpSelectors.getXtps(state),
  xtpOptions: xtpSelectors.getXtpOptions(state),
  xtpErrors: xtpSelectors.getXtpErrors(state),
  uiState: xtpSelectors.getUiState(state),
  formTemplate: adaptTemplateVisibilityMode(
    xtpTemplate,
    'edit',
  ),
});

const mapDispatch = {
  getXtps: xtpActions.getXtps,
  goToXtpView: xtpActions.goToXtpView,
  updateXtp: xtpActions.updateXtp,
  getXtpOptions: xtpActions.getXtpOptions,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const XtpEdit = ({
  history,
  xtps,
  xtpOptions,
  deliveryFunctionId,
  formTemplate,
  isAuditEnabled,
  userId,
  userName,
  xtpErrors,
  uiState,
  getXtps,
  goToXtpView,
  updateXtp,
  getXtpOptions,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [template, setTemplate] = useState(formTemplate);
  const [formData, setFormData] = useState<Partial<{ xtp: xtpTypes.Xtp, xtpOptions: xtpTypes.XtpOptions}>>({});
  const formRef = useRef<Ref>(null);

  useEffect(() => {
    if (deliveryFunctionId == null) {
      return;
    }
    getXtps(deliveryFunctionId)
    getXtpOptions(deliveryFunctionId)
  }, [deliveryFunctionId, getXtps, getXtpOptions]);

  useEffect(() => {
    const clonedXtp: xtpTypes.Xtp = cloneDeep(xtps[0]);
    const cloneXtpOptions:xtpTypes.XtpOptions = cloneDeep(xtpOptions[0]);
    const newFormData:Partial<{ xtp: xtpTypes.Xtp, xtpOptions: xtpTypes.XtpOptions}> = {};
    const clonedTemplate = cloneDeep(template);
    if (clonedTemplate != null) {
      clonedTemplate.key = Date.now().toString();
    }

    if (clonedXtp != null) {
      newFormData.xtp = clonedXtp;
    }

    if (cloneXtpOptions != null) {
      newFormData.xtpOptions = cloneXtpOptions;
    }

    setFormData(newFormData);
    setTemplate(clonedTemplate);
    console.log(xtps, xtpOptions)
  }, [xtps, xtpOptions]);

  const handleClickCancel = () => {
    goToXtpView(history);
  };

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam, formSection?: 'main' | 'xtpOptions') => {
    if (template == null || formData == null || formData.xtp == null || formData.xtpOptions == null) {
      return;
    }

    const clonedTemplate = cloneDeep(template);
    if (clonedTemplate!= null) {
      clonedTemplate.key = Date.now().toString();
    }

    let xtpData = cloneDeep(formData.xtp) as xtpTypes.Xtp;
    let xtpOptionsData = cloneDeep(formData.xtpOptions) as xtpTypes.XtpOptions;

    if (formSection === 'main') {
      xtpData = {
        ...xtpData,
        [fieldName]: value,
      };
    } else {
      xtpOptionsData = {
        ...xtpOptionsData,
        [fieldName]: value,
      };
    }

    setTemplate(clonedTemplate);
    setFormData({
      xtp: xtpData,
      xtpOptions: xtpOptionsData,
    });
  };

  const handleClickSave = () => {
    const formData = formRef.current?.getValues();
    const isValid = formRef.current?.isValid(true, true);

    if (!formData || !isValid) {
      return;
    }

    const xtpFormData = getXtpDataToSubmit(formData as xtpTypes.XtpFormData);

    const xtpAuditDetails = getXtpAuditDetailsToSubmit(
      xtpFormData.main,
      null,
      isAuditEnabled,
      userId,
      userName,
      'CREATE',
    );

    const xtpOptionsAuditDetails = getXtpOptionsAuditDetailsToSubmit(
      xtpFormData.xtpOptions,
      null,
      isAuditEnabled,
      userId,
      userName,
      'UPDATE',
    );

    const xtpPayload = {
      data: xtpFormData.main,
      auditDetails: xtpAuditDetails
    }

    const xtpOptionsPayload = {
      data: xtpFormData.xtpOptions,
      auditDetails: xtpOptionsAuditDetails
    }

    updateXtp(deliveryFunctionId, xtpPayload, xtpOptionsPayload, history);
  };

  const renderForm = () => {
    if (!template || uiState.isLoading) {
      return null;
    }

    return (
      <div>
        <XtpForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      </div>
    );
  };

  const renderFormPanel = () => {
    return (
      <FormPanel title={t('xtp.edit.title')}>
        <FormButtons
          isEditing
          onClickSave={handleClickSave}
          onClickCancel={handleClickCancel}
        />
      </FormPanel>
    );
  };

  if (uiState.isLoading) {
    return <div />;
  }

  return (
    <div data-testid="XtpEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(XtpEdit));
